
import moment from 'moment';

import {
  defineComponent,
  defineAsyncComponent,
  ref,
  reactive,
  watch,
  onMounted,
} from 'vue';

import { useRoute } from 'vue-router';

import type {
  GenericObject,
  Campaigns,
  Creatives,
  Countries,
  Platforms,
  Browsers,
  Statistics,
} from '@/types';

import {
  TRAFFIC_CATEGORIES,
  TRAFFIC_TYPES,
} from '@/libs/consts';

import useEntriesNext from '@/composable/useEntries@next';

import useTimezonePreference from '@/composable/useTimezonePreference';

import useStatisticsLink from '@/composable/useStatisticsLink';

import useSubStatisticsComponent from '@/composable/useSubStatisticsComponent';

export default defineComponent({
  components: {
    Actions: defineAsyncComponent(() => import('@/components/Statistics/Advertiser/Actions.vue')),
    ViewsColumn: defineAsyncComponent(() => import('@/components/Statistics/Advertiser/Columns/ViewsColumn.vue')),
    StatisticsByHours: defineAsyncComponent(() => import('@/components/Statistics/Advertiser/StatisticsByHours.vue')),
    StatisticsByCampaigns: defineAsyncComponent(() => import('@/components/Statistics/Advertiser/StatisticsByCampaigns.vue')),
    StatisticsByCreatives: defineAsyncComponent(() => import('@/components/Statistics/Advertiser/StatisticsByCreatives.vue')),
    StatisticsByZones: defineAsyncComponent(() => import('@/components/Statistics/Advertiser/StatisticsByZones.vue')),
    StatisticsBySubids: defineAsyncComponent(() => import('@/components/Statistics/Advertiser/StatisticsBySubids.vue')),
    StatisticsByCountries: defineAsyncComponent(() => import('@/components/Statistics/Advertiser/StatisticsByCountries.vue')),
    StatisticsByPlatforms: defineAsyncComponent(() => import('@/components/Statistics/Advertiser/StatisticsByPlatforms.vue')),
    StatisticsByBrowsers: defineAsyncComponent(() => import('@/components/Statistics/Advertiser/StatisticsByBrowsers.vue')),
  },
  setup() {
    const route = useRoute();

    const {
      entries: campaigns,
      entriesLoading: campaignsLoading,
      fetchEntries: fetchCampaigns,
    } = useEntriesNext<Campaigns>('/api/campaign/getCampaigns');

    const {
      entries: creatives,
      entriesLoading: creativesLoading,
      fetchEntries: fetchCreatives,
    } = useEntriesNext<Creatives>('/api/creative/getCreatives');

    const {
      entries: countries,
      entriesLoading: countriesLoading,
      fetchEntries: fetchCountries,
    } = useEntriesNext<Countries>('/api/country/getCountries');

    const {
      entries: platforms,
      entriesLoading: platformsLoading,
      fetchEntries: fetchPlatforms,
    } = useEntriesNext<Platforms>('/api/platform/getPlatforms');

    const {
      entries: browsers,
      entriesLoading: browsersLoading,
      fetchEntries: fetchBrowsers,
    } = useEntriesNext<Browsers>('/api/browser/getBrowsers');

    const {
      entries: statistics,
      entriesLoading: statisticsLoading,
      entriesFetched: statisticsFetched,
      fetchEntries: fetchStatistics,
    } = useEntriesNext<Statistics>('/api/statistics/advertiser/getStatisticsByDays');

    const { savedTimezone, watchTimezone } = useTimezonePreference();

    const {
      encodeStatisticsFilters,
      decodeStatisticsFilters,
    } = useStatisticsLink();

    const {
      subStatisticsComponent,
      setSubStatisticsComponent,
      resetSubStatisticComponent,
    } = useSubStatisticsComponent();

    const getStatisticByDayRowClass = (row: GenericObject): string => (row.date === subStatisticsComponent.filters.date ? 'statistics-table-row-active' : '');

    const defaultStatisticsFilters = {
      date_from: moment().subtract(4, 'days').format('YYYY-MM-DD'),
      date_to: moment().format('YYYY-MM-DD'),
      timezone: savedTimezone,
      traffic_type: null,
      traffic_category: null,
      campaign_folder_id: [],
      campaign_id: null,
      creative_id: null,
      country_id: null,
      platform_id: null,
      browser_id: null,
    };

    const queryStatisticsFilters = {} as GenericObject;

    if (route.query.filters) {
      Object.assign(queryStatisticsFilters, decodeStatisticsFilters(route.query.filters as string));
    }

    const statisticsFilters = reactive<GenericObject>({
      ...defaultStatisticsFilters,
      ...queryStatisticsFilters,
    });

    watchTimezone(() => statisticsFilters.timezone);

    watch(
      statisticsFilters,
      (value: GenericObject) => {
        const encodedStatisticsFilters = encodeStatisticsFilters(value);

        // eslint-disable-next-line no-restricted-globals
        history.pushState(
          {},
          '',
          `${route.path}?${new URLSearchParams({ filters: encodedStatisticsFilters }).toString()}`,
        );
      },
    );

    const statisticsFiltersKeyCounter = ref<number>(0);

    const resetStatisticsFilters = (): void => {
      Object.assign(statisticsFilters, defaultStatisticsFilters);

      statisticsFiltersKeyCounter.value += 1;
    };

    const fetchStatisticsWrapper = async (): Promise<void> => {
      fetchStatistics(statisticsFilters);

      resetSubStatisticComponent();
    };

    onMounted(() => Promise.all([
      fetchCampaigns(),
      fetchCreatives(),
      fetchCountries(),
      fetchPlatforms(),
      fetchBrowsers(),
    ]));

    return {
      TRAFFIC_CATEGORIES,
      TRAFFIC_TYPES,
      campaigns,
      campaignsLoading,
      creatives,
      creativesLoading,
      countries,
      countriesLoading,
      platforms,
      platformsLoading,
      browsers,
      browsersLoading,
      statistics,
      statisticsLoading,
      statisticsFetched,
      statisticsFilters,
      statisticsFiltersKeyCounter,
      resetStatisticsFilters,
      fetchStatisticsWrapper,
      subStatisticsComponent,
      setSubStatisticsComponent,
      getStatisticByDayRowClass,
    };
  },
});
